import { useMutation } from '@tanstack/react-query'
import { Avatar, Button, Dropdown, Typography, type MenuProps } from 'antd'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { usersPartialUpdateMutation, type LanguageEnum } from 'src/api/main'
import { useSessionContext } from 'src/hooks'
import { allLangs } from 'src/locales'
import { useShowApiErrors } from 'src/utils'

const flagSrc = (icon: string) => `/flags/${icon}`

export const LanguageSwitcher = () => {
  const { i18n } = useTranslation()
  const { user, activeWorkspaceId, updateUserData } = useSessionContext()
  const showApiErrors = useShowApiErrors()
  const languageUpdate = useMutation(usersPartialUpdateMutation())

  /**
   * Only languages the active workspace enables, the same constraint the
   * profile settings apply (TS-CORE-027) — offering one it does not enable
   * meant the resolver bounced the UI straight back to the workspace default,
   * so the control appeared to refuse the click. No session or no constraint
   * (the public auth screens): everything is on offer.
   */
  const languages = useMemo(() => {
    const currentOrg =
      user?.organizations?.find(
        (organization) => Number(organization.id) === activeWorkspaceId
      ) ?? user?.organizations?.[0]
    const enabled = currentOrg?.enabled_languages

    return enabled?.length
      ? allLangs.filter((lang) => enabled.includes(lang.value))
      : allLangs
  }, [user, activeWorkspaceId])

  /**
   * The language turns over on the click, always. Signed out that is the whole
   * job — there is no profile to write and the detector cache keeps the choice,
   * which is why `/login` and `/register` have always felt instant.
   *
   * Signed in, the session-level resolver (TS-CORE-027) treats the *cached*
   * session's language as authoritative and re-asserts it whenever that query
   * changes. So the cache is corrected up front rather than from the response:
   * waiting for the round-trip left a window in which any refresh of the
   * session — including the one the write itself used to trigger — put the old
   * language back, which is the flicker that read as the switch not taking.
   * The request is a background echo of a decision already applied; only its
   * failure is worth reacting to.
   */
  const pick = (value: LanguageEnum) => {
    void i18n.changeLanguage(value)

    const profileId = user?.user?.id
    if (!user || profileId == null || user.user.language === value) return

    const previous = user.user.language
    updateUserData({ ...user, user: { ...user.user, language: value } })

    languageUpdate.mutate(
      { path: { id: profileId }, body: { language: value } },
      {
        onError: (error) => {
          // Hand the cache back its old value and let the resolver realign the
          // UI from it — the language the app shows must not outlive the claim
          // that it was saved.
          updateUserData({
            ...user,
            user: { ...user.user, language: previous }
          })
          showApiErrors(error)
        }
      }
    )
  }

  const current =
    allLangs.find((l) => l.value === i18n.language.split('-')[0]) ?? allLangs[0]

  const menuItems: MenuProps['items'] = languages.map((lang) => ({
    key: lang.value,
    onClick: () => pick(lang.value),
    icon: <Avatar size={18} src={flagSrc(lang.icon)} />,
    label: <Typography.Text strong>{lang.label}</Typography.Text>
  }))

  return (
    <Dropdown
      menu={{
        items: menuItems,
        selectedKeys: [current?.value]
      }}
      placement="bottom"
    >
      <Button
        type="text"
        icon={<Avatar size={18} src={flagSrc(current.icon)} />}
      >
        <Typography.Text strong>{current?.value.toUpperCase()}</Typography.Text>
      </Button>
    </Dropdown>
  )
}
